/**
 * PurchaseConfirmation - shown right after a membership payment succeeds,
 * before the fan continues into the site: what was bought, the amount, the
 * date, whether it renews, when access ends (or renews), and the card used.
 * (A downloadable receipt is planned separately.)
 */

import { LEGAL, LEGAL_STYLE } from '@/config/legalConfig'

export interface PaidInfo {
  plan: string
  amount: string                 // "$50.00"
  paidAt: Date
  renewal: string                // "No auto renewal" | "Renews automatically every month"
  endLabel: string               // "Access Expires" | "Next Renewal"
  endsAt: Date | null
  card?: { brand?: string | null; last4?: string | null } | null
  welcome: string
}

const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const brandName = (b?: string | null) => {
  if (!b) return 'Card'
  const m: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay' }
  return m[b.toLowerCase()] || b.charAt(0).toUpperCase() + b.slice(1)
}

/** Access end date: the server's date when it sent one, otherwise today + days. */
export function accessEnd(serverDate: string | null | undefined, days: number): Date {
  const d = serverDate ? new Date(serverDate) : null
  return d && !isNaN(d.getTime()) ? d : new Date(Date.now() + days * 86400000)
}

export function PurchaseConfirmation({ info, onContinue, continueLabel = 'Continue' }: { info: PaidInfo; onContinue: () => void; continueLabel?: string }) {
  const rows: [string, string][] = [
    ['Plan', info.plan],
    ['Amount Paid', info.amount],
    ['Payment Date', fmt(info.paidAt)],
    ['Renewal', info.renewal],
  ]
  if (info.endsAt) rows.push([info.endLabel, fmt(info.endsAt)])
  if (info.card?.last4) rows.push(['Payment Method', `${brandName(info.card.brand)} •••• ${info.card.last4}`])

  return (
    <div className="space-y-6 text-center" data-testid="purchase-confirmation">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-md">
        <svg className="h-9 w-9 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Payment Successful!</h2>
        <p className="mt-1 text-gray-600">{info.welcome}</p>
      </div>

      <dl className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white text-left text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 px-4 py-3">
            <dt className="text-gray-600">{k}</dt>
            <dd className="font-medium text-gray-900 text-right tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        data-testid="confirmation-continue"
        onClick={onContinue}
        className={`w-full rounded-lg px-6 py-3 font-semibold shadow-md transition-colors ${LEGAL_STYLE.button}`}
      >
        {continueLabel}
      </button>

      <p className="text-xs text-gray-500">
        Keep this for your records. Questions about this payment? {LEGAL.supportEmail}
      </p>
    </div>
  )
}
